import { AppIcon, type College } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Chip, SxProps, Theme, Tooltip } from '@mui/material';

import { TIER_COLORS, type TierColors } from './tierColors';
import { myCollegeTooltip, useMyCollegeToggle } from './useMyCollegeToggle';

const STRINGS = appStrings.modules.myColleges;

type AddIconChipProps = Readonly<{
  id: string;
  college: Pick<College, 'academic_profile_id' | 'ipeds_id' | 'school_name'>;
  colors?: TierColors;
}>;

export const AddIconChip = ({ id, college, colors }: AddIconChipProps) => {
  const myCollege = useMyCollegeToggle(college);
  const { isAvailable, isInMyColleges, tier, toggle } = myCollege;

  if (!isAvailable) return null;

  const tooltip = myCollegeTooltip(myCollege) || STRINGS.add;

  return (
    <Tooltip title={tooltip}>
      <Chip
        id={id}
        variant="pill"
        size="sm"
        type="only"
        aria-label={isInMyColleges ? STRINGS.removeAriaLabel(college.school_name) : STRINGS.add}
        onClick={toggle}
        sx={addIconChipSx(isInMyColleges ? (colors ?? TIER_COLORS[tier ?? 'none']) : undefined)}
        icon={
          <AppIcon
            iconName={isInMyColleges ? 'Check' : 'Plus'}
            diameter="12px"
            color="currentColor"
          />
        }
      />
    </Tooltip>
  );
};

const addIconChipSx =
  (colors: TierColors | undefined): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    flexShrink: 0,
    '& .MuiChip-label': { display: 'none' },
    backgroundColor: backrs.colors[colors?.bg ?? 'GrayBlue/50'],
    borderColor: backrs.colors[colors?.border ?? 'GrayBlue/200'],
    color: backrs.colors[colors?.text ?? 'GrayBlue/500'],
  });
